import { Box, Button, Link, Stack, Typography } from "@mui/material";

import type {
  ActionsBlock,
  ImageBlock,
  KvBlock,
  NotificationBlock,
  TableBlock,
  TextBlock,
} from "./schema";
import {
  isSafeNotificationUrl,
  useOpenNotificationUrl,
} from "./useOpenNotificationUrl";

import { ArrowForwardIcon } from "@/icons";

interface BlockRendererProps {
  blocks: NotificationBlock[];
  onOpenUrl?: (url: string) => void;
  "data-testid"?: string;
}

export function BlockRenderer({
  blocks,
  onOpenUrl,
  "data-testid": dataTestId = "notification-blocks",
}: BlockRendererProps) {
  if (blocks.length === 0) return null;
  return (
    <Stack spacing={3} data-testid={dataTestId}>
      {blocks.map((block, index) => (
        <Block
          key={`${block.type}-${index}`}
          block={block}
          onOpenUrl={onOpenUrl}
        />
      ))}
    </Stack>
  );
}

interface BlockProps {
  block: NotificationBlock;
  onOpenUrl?: (url: string) => void;
}

function Block({ block, onOpenUrl }: BlockProps) {
  switch (block.type) {
    case "text":
      return <Text block={block} onOpenUrl={onOpenUrl} />;
    case "kv":
      return <Kv block={block} />;
    case "table":
      return <Table block={block} />;
    case "image":
      return <Image block={block} />;
    case "actions":
      return <Actions block={block} onOpenUrl={onOpenUrl} />;
  }
}

function useOpenBlockUrl(onOpenUrl?: (url: string) => void) {
  const openUrl = useOpenNotificationUrl();
  return (url: string) => {
    if (!isSafeNotificationUrl(url)) return;
    onOpenUrl?.(url);
    openUrl(url);
  };
}

function Text({
  block,
  onOpenUrl,
}: {
  block: TextBlock;
  onOpenUrl?: (url: string) => void;
}) {
  const openUrl = useOpenBlockUrl(onOpenUrl);
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      data-testid="notification-block-text"
      sx={{ overflowWrap: "anywhere" }}
    >
      {block.spans.map((span, index) => {
        const content = span.bold ? (
          <Box
            component="strong"
            sx={{ fontWeight: "fontWeightMedium", color: "text.primary" }}
          >
            {span.text}
          </Box>
        ) : (
          span.text
        );
        if (!span.href || !isSafeNotificationUrl(span.href))
          return <span key={index}>{content}</span>;
        return (
          <Link
            key={index}
            href={span.href}
            underline="always"
            onClick={(event) => {
              event.preventDefault();
              openUrl(span.href);
            }}
          >
            {content}
          </Link>
        );
      })}
    </Typography>
  );
}

function Kv({ block }: { block: KvBlock }) {
  return (
    <Box
      data-testid="notification-block-kv"
      sx={{
        display: "grid",
        gridTemplateColumns: "minmax(72px, auto) 1fr",
        columnGap: 5,
        rowGap: 2,
        px: 4,
        py: 3,
        borderRadius: (theme) => `${theme.shape.borderRadiusSm}px`,
        backgroundColor: "background.paper-elevation-1",
      }}
    >
      {block.items.map((item, index) => (
        <Box key={index} sx={{ display: "contents" }}>
          <Typography variant="body2" color="text.secondary">
            {item.key}
          </Typography>
          <Typography
            variant="body2"
            color="text.primary"
            sx={{ fontWeight: "fontWeightMedium", overflowWrap: "anywhere" }}
          >
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function Table({ block }: { block: TableBlock }) {
  return (
    <Box sx={{ overflowX: "auto" }}>
      <Box
        component="table"
        data-testid="notification-block-table"
        sx={{
          width: "100%",
          borderCollapse: "collapse",
          "& th, & td": {
            textAlign: "left",
            py: 2,
            px: 3,
            borderBottom: 1,
            borderColor: "divider",
            whiteSpace: "nowrap",
          },
          "& tr:last-of-type td": { borderBottom: 0 },
        }}
      >
        <Box component="thead">
          <Box component="tr">
            {block.columns.map((column) => (
              <Typography
                key={column}
                component="th"
                variant="subtitle3"
                color="text.secondary"
              >
                {column}
              </Typography>
            ))}
          </Box>
        </Box>
        <Box component="tbody">
          {block.rows.map((row, rowIndex) => (
            <Box component="tr" key={rowIndex}>
              {block.columns.map((column, cellIndex) => (
                <Typography
                  key={column}
                  component="td"
                  variant="body2"
                  color="text.primary"
                >
                  {row.cells[cellIndex] ?? ""}
                </Typography>
              ))}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function Image({ block }: { block: ImageBlock }) {
  if (!isSafeNotificationUrl(block.url)) return null;
  return (
    <Box
      component="img"
      data-testid="notification-block-image"
      src={block.url}
      alt={block.alt}
      sx={{
        display: "block",
        maxWidth: "100%",
        height: "auto",
        borderRadius: (theme) => `${theme.shape.borderRadiusMd}px`,
      }}
    />
  );
}

function Actions({
  block,
  onOpenUrl,
}: {
  block: ActionsBlock;
  onOpenUrl?: (url: string) => void;
}) {
  const openUrl = useOpenBlockUrl(onOpenUrl);
  return (
    <Stack
      direction="row"
      spacing={4}
      data-testid="notification-block-actions"
      sx={{ flexWrap: "wrap", alignItems: "center" }}
    >
      {block.items.map((item, index) => (
        <Button
          key={item.label}
          size="small"
          variant="text"
          endIcon={index === 0 ? <ArrowForwardIcon /> : undefined}
          onClick={() => openUrl(item.url)}
          sx={{ px: 3, py: 2, minWidth: 0 }}
        >
          {item.label}
        </Button>
      ))}
    </Stack>
  );
}
